import { UserFriendlyError } from '@affine/error';
import { useCallback, useEffect, useState } from 'react';

export interface SettingsEntityListState<T> {
  data: T[] | undefined;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useSettingsEntityList<T>(
  fetcher: () => Promise<T[]>,
  deps: ReadonlyArray<unknown>
): SettingsEntityListState<T> {
  const [data, setData] = useState<T[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetcher();
      setData(result);
    } catch (err) {
      setError(UserFriendlyError.fromAny(err).message);
    } finally {
      setIsLoading(false);
    }
    // Hook contract: caller controls refetch via `deps`. `fetcher` is intentionally
    // excluded — consumers typically pass inline closures that change every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, isLoading, error, refresh };
}
